
import React from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';
import { SortableCard } from "@/components/SortableCard";
import { Section } from '@/types/sections';

interface SortableCardSection {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  editable?: boolean;
  canDelete?: boolean;
  prompt?: string;
}

interface SectionsGridProps {
  sections: Section[];
  onSectionsChange: (sections: Section[]) => void;
  onEditCard: (section: Section) => void;
  onDeleteCard: (section: Section) => void;
  onDragEnd: (event: any) => void;
}

export const SectionsGrid = ({
  sections,
  onSectionsChange,
  onEditCard,
  onDeleteCard,
  onDragEnd
}: SectionsGridProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Convert sections to format expected by SortableCard
  const convertedSections: SortableCardSection[] = sections.map(section => ({
    id: section.id.toString(),
    title: section.title,
    description: section.description,
    checked: section.checked,
    editable: section.editable,
    canDelete: section.canDelete,
    prompt: section.prompt
  }));

  // Create a mock templateCards object for the SortableCard component
  const templateCards = {
    sections: convertedSections,
  };

  const setTemplateCards = (newTemplateCards: any) => {
    const updatedSections = sections.map(section => {
      const updatedCard = newTemplateCards.sections.find((card: any) => card.id === section.id.toString());
      if (updatedCard) {
        return { ...section, checked: updatedCard.checked };
      }
      return section;
    });
    onSectionsChange(updatedSections);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={convertedSections.map((section) => section.id)} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {convertedSections.map((section) => {
            const originalSection = sections.find(s => s.id.toString() === section.id);
            return (
              <SortableCard
                key={section.id}
                card={section}
                handleEditCard={() => originalSection && onEditCard(originalSection)}
                handleDeleteCard={() => originalSection && onDeleteCard(originalSection)}
                setTemplateCards={setTemplateCards}
                templateCards={templateCards}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
};
